// src/ProtectedRoute.js
import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";

// ✅ Shared flag at module scope — this persists between component instances
let logoutInProgress = false;

function ProtectedRoute({ children, allowedRoles = null }) {
  const [tokenChecked, setTokenChecked] = useState(false);
  const [hasToken, setHasToken] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    setHasToken(!!token);
    
    // Get user role from localStorage - match backend logic
    try {
      const userStr = localStorage.getItem("user");
      if (userStr && userStr !== "undefined" && userStr !== "null") {
        const user = JSON.parse(userStr);
        let role = "Guest";
        if (user) {
          if (user.is_superuser) {
            role = "Admin";
          } else if (user.role) {
            role = user.role;
          } else if (user.groups && Array.isArray(user.groups) && user.groups.length > 0) {
            const groupName = user.groups[0]?.name || user.groups[0];
            if (groupName) {
              role = groupName;
            }
          }
        }
        setUserRole(normalizeRole(role));
      }
    } catch (err) {
      console.error("Failed to parse user from localStorage:", err);
    }
    
    setTokenChecked(true);
  }, []);

  // While checking token, render nothing (prevents flash of protected UI)
  if (!tokenChecked) return null;

  // ✅ If user is logged out or in forced logout sequence
  if (!hasToken) {
    if (!logoutInProgress) {
      logoutInProgress = true;

      // Optional: remove any stale data (ensure clean state)
      localStorage.removeItem("user");
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");

      console.warn("🔒 Missing/expired token, redirecting to login...");
    }

    return (
      <Navigate
        to={`/login?next=${encodeURIComponent(location.pathname)}`}
        replace
      />
    );
  }

  // ✅ Check role-based access if allowedRoles is specified
  if (allowedRoles && Array.isArray(allowedRoles) && allowedRoles.length > 0) {
    if (!userRole || !allowedRoles.includes(userRole)) {
      // Redirect to dashboard if role not allowed
      return <Navigate to="/dashboard" replace />;
    }
  }

  // ✅ Once a valid token reappears, clear the flag so login works again
  if (logoutInProgress) logoutInProgress = false;

  return children;
}

// Normalize role names to match backend expectations
function normalizeRole(role) {
  if (!role) return "Guest";
  
  const roleLower = role.toLowerCase().trim();
  
  // Map variations to standard role names
  if (roleLower === "admin" || roleLower === "administrator") {
    return "Admin";
  }
  if (roleLower.includes("sales director") || roleLower === "director") {
    return "Sales Director";
  }
  if (roleLower.includes("manager") || roleLower === "territory manager" || roleLower === "regional manager") {
    return "Manager";
  }
  if (roleLower.includes("sales rep") || roleLower === "rep" || roleLower === "sales representative") {
    return "Sales Rep";
  }
  
  // Return capitalized version if no match
  return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
}

export default ProtectedRoute;
